import React, { useState, useEffect } from "react";
import {
  FaToggleOn,
  FaToggleOff,
  FaSave,
  FaChartBar,
  FaEye,
  FaEyeSlash,
  FaPercent,
  FaClock,
  FaFire,
  FaUsers,
} from "react-icons/fa";
import { getFomoSettings, saveFomoSettings } from "../firebase";
import { logAdminActivity } from "../utils/adminSecurity";
import "./FomoSettings.css";

const FomoSettings = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    hidePercentage: 30, // Gizlenecek slot yüzdesi
    upcomingDaysOnly: true, // Sadece önümüzdeki 1-3 gün için uygula
    showFomoIndicators: true, // FOMO göstergelerini göster
    showAppointmentCounter: true, // Son 7 günde alınan randevu sayısını göster
    popularHours: ["10:00", "11:00", "14:00", "15:00"], // Popüler saatler
    lowSlotsThreshold: 3, // "Az slot kaldı" uyarısı için eşik
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await getFomoSettings();
      if (data) {
        setSettings({ ...settings, ...data });
      }
    } catch (error) {
      console.error("FOMO ayarları yüklenirken hata:", error);
      setMessage("Ayarlar yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    try {
      const previousSettings = { ...settings };

      await saveFomoSettings(settings);
      setMessage("FOMO ayarları başarıyla kaydedildi!");
      setTimeout(() => setMessage(""), 3000);

      // Log FOMO settings change
      logAdminActivity("fomo_settings_change", {
        previousSettings: {
          enabled: previousSettings.enabled,
          hidePercentage: previousSettings.hidePercentage,
          upcomingDaysOnly: previousSettings.upcomingDaysOnly,
          showFomoIndicators: previousSettings.showFomoIndicators,
          showAppointmentCounter: previousSettings.showAppointmentCounter,
          popularHours: previousSettings.popularHours,
          lowSlotsThreshold: previousSettings.lowSlotsThreshold,
        },
        newSettings: {
          enabled: settings.enabled,
          hidePercentage: settings.hidePercentage,
          upcomingDaysOnly: settings.upcomingDaysOnly,
          showFomoIndicators: settings.showFomoIndicators,
          showAppointmentCounter: settings.showAppointmentCounter,
          popularHours: settings.popularHours,
          lowSlotsThreshold: settings.lowSlotsThreshold,
        },
        timestamp: new Date().toISOString(),
        changedFields: getChangedFields(previousSettings, settings),
      });
    } catch (error) {
      console.error("FOMO ayarları kaydedilirken hata:", error);
      setMessage("Ayarlar kaydedilirken hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const getChangedFields = (previous, current) => {
    const changed = [];
    Object.keys(current).forEach((key) => {
      if (JSON.stringify(previous[key]) !== JSON.stringify(current[key])) {
        changed.push(key);
      }
    });
    return changed;
  };

  const handleToggle = (field) => {
    setSettings((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSliderChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: parseInt(value),
    }));
  };

  const handlePopularHourToggle = (hour) => {
    setSettings((prev) => {
      const popularHours = prev.popularHours.includes(hour)
        ? prev.popularHours.filter((h) => h !== hour)
        : [...prev.popularHours, hour];
      return { ...prev, popularHours };
    });
  };

  const timeSlots = [
    "09:00",
    "09:30",
    "10:00",
    "10:30",
    "11:00",
    "11:30",
    "12:00",
    "12:30",
    "13:00",
    "13:30",
    "14:00",
    "14:30",
    "15:00",
    "15:30",
    "16:00",
    "16:30",
    "17:00",
  ];

  if (loading) {
    return (
      <div className="fomo-settings loading">
        <div className="loading-spinner"></div>
        <p>FOMO ayarları yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="fomo-settings">
      <div className="fomo-header">
        <h2>
          <FaChartBar className="header-icon" />
          FOMO Ayarları
        </h2>
        <p>Sahte doluluk ve FOMO etkisi ayarlarını yönetin</p>
      </div>

      {message && (
        <div
          className={`message ${
            message.includes("başarıyla") ? "success" : "error"
          }`}
        >
          {message}
        </div>
      )}

      <div className="settings-container">
        {/* Ana FOMO Ayarı */}
        <div className="setting-section">
          <div className="setting-item main-toggle">
            <div className="setting-info">
              <h3>
                {settings.enabled ? <FaEye /> : <FaEyeSlash />}
                Sahte Doluluk Özelliği
              </h3>
              <p>
                Bu özellik aktif edildiğinde, kullanıcılara bazı randevu
                slotları gizli gösterilir. Bu sayede FOMO (Kaçırma Korkusu)
                etkisi yaratılır.
              </p>
            </div>
            <button
              className={`toggle-btn ${settings.enabled ? "active" : ""}`}
              onClick={() => handleToggle("enabled")}
            >
              {settings.enabled ? <FaToggleOn /> : <FaToggleOff />}
              {settings.enabled ? "Aktif" : "Pasif"}
            </button>
          </div>
        </div>

        {settings.enabled && (
          <>
            {/* Gizleme Yüzdesi */}
            <div className="setting-section">
              <div className="setting-item">
                <div className="setting-info">
                  <h4>
                    <FaPercent />
                    Gizlenecek Slot Yüzdesi
                  </h4>
                  <p>
                    Müsait slotların yüzde kaçı gizli gösterilecek. Örneğin: %30
                    seçilirse, 10 müsait slottan 3'ü gizlenecek.
                  </p>
                </div>
                <div className="slider-container">
                  <input
                    type="range"
                    min="10"
                    max="70"
                    step="10"
                    value={settings.hidePercentage}
                    onChange={(e) =>
                      handleSliderChange("hidePercentage", e.target.value)
                    }
                    className="slider"
                  />
                  <span className="slider-value">
                    %{settings.hidePercentage}
                  </span>
                </div>
              </div>
            </div>

            {/* Uygulama Zamanı */}
            <div className="setting-section">
              <div className="setting-item">
                <div className="setting-info">
                  <h4>
                    <FaClock />
                    Sadece Önümüzdeki Günler
                  </h4>
                  <p>
                    Bu özellik sadece önümüzdeki 1-3 gün için mi uygulanacak?
                    Uzak tarihler için normal gösterim yapılır.
                  </p>
                </div>
                <button
                  className={`toggle-btn small ${
                    settings.upcomingDaysOnly ? "active" : ""
                  }`}
                  onClick={() => handleToggle("upcomingDaysOnly")}
                >
                  {settings.upcomingDaysOnly ? <FaToggleOn /> : <FaToggleOff />}
                  {settings.upcomingDaysOnly
                    ? "Sadece Yakın Günler"
                    : "Tüm Günler"}
                </button>
              </div>
            </div>

            {/* FOMO Göstergeleri */}
            <div className="setting-section">
              <div className="setting-item">
                <div className="setting-info">
                  <h4>
                    <FaFire />
                    FOMO Göstergeleri
                  </h4>
                  <p>
                    "Az slot kaldı", "Popüler saat" gibi FOMO tetikleyici
                    mesajlar gösterilsin mi?
                  </p>
                </div>
                <button
                  className={`toggle-btn small ${
                    settings.showFomoIndicators ? "active" : ""
                  }`}
                  onClick={() => handleToggle("showFomoIndicators")}
                >
                  {settings.showFomoIndicators ? (
                    <FaToggleOn />
                  ) : (
                    <FaToggleOff />
                  )}
                  {settings.showFomoIndicators ? "Göster" : "Gizle"}
                </button>
              </div>
            </div>

            {settings.showFomoIndicators && (
              <>
                {/* Randevu Sayacı */}
                <div className="setting-section">
                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>
                        <FaChartBar />
                        Randevu Sayacı
                      </h4>
                      <p>
                        "Son 7 günde X randevu alındı" gibi bir mesaj
                        gösterilsin mi? Bu sayı tutarlı ve gerçekçi bir şekilde
                        hesaplanır.
                      </p>
                    </div>
                    <button
                      className={`toggle-btn small ${
                        settings.showAppointmentCounter ? "active" : ""
                      }`}
                      onClick={() => handleToggle("showAppointmentCounter")}
                    >
                      {settings.showAppointmentCounter ? (
                        <FaToggleOn />
                      ) : (
                        <FaToggleOff />
                      )}
                      {settings.showAppointmentCounter ? "Göster" : "Gizle"}
                    </button>
                  </div>
                </div>

                {/* Az Slot Eşiği */}
                <div className="setting-section">
                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>
                        <FaUsers />
                        "Az Slot Kaldı" Eşiği
                      </h4>
                      <p>
                        Kaç slot kaldığında "Az slot kaldı!" uyarısı
                        gösterilsin?
                      </p>
                    </div>
                    <div className="slider-container">
                      <input
                        type="range"
                        min="1"
                        max="5"
                        step="1"
                        value={settings.lowSlotsThreshold}
                        onChange={(e) =>
                          handleSliderChange(
                            "lowSlotsThreshold",
                            e.target.value
                          )
                        }
                        className="slider"
                      />
                      <span className="slider-value">
                        {settings.lowSlotsThreshold} slot
                      </span>
                    </div>
                  </div>
                </div>

                {/* Popüler Saatler */}
                <div className="setting-section">
                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>
                        <FaFire />
                        Popüler Saatler
                      </h4>
                      <p>
                        Hangi saatler "Popüler saat!" olarak işaretlensin? Bu
                        saatler özel vurgulanacak.
                      </p>
                    </div>
                    <div className="popular-hours-grid">
                      {timeSlots.map((hour) => (
                        <button
                          key={hour}
                          className={`hour-btn ${
                            settings.popularHours.includes(hour)
                              ? "selected"
                              : ""
                          }`}
                          onClick={() => handlePopularHourToggle(hour)}
                        >
                          {hour}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Kaydet Butonu */}
      <div className="save-section">
        <button className="save-btn" onClick={handleSave} disabled={saving}>
          <FaSave />
          {saving ? "Kaydediliyor..." : "Ayarları Kaydet"}
        </button>
      </div>

      {/* Bilgilendirme */}
      <div className="info-section">
        <h4>📈 FOMO Etkisi Nasıl Çalışır?</h4>
        <ul>
          <li>
            <strong>Sahte Doluluk:</strong> Gerçekte müsait olan slotların bir
            kısmı gizlenir. Bu da kullanıcılarda "çabuk davranmalıyım" hissi
            yaratır.
          </li>
          <li>
            <strong>Popüler Saatler:</strong> Belirlediğiniz saatler özel olarak
            vurgulanır ve "Popüler saat!" etiketi alır.
          </li>
          <li>
            <strong>Az Slot Uyarısı:</strong> Slot sayısı belirlediğiniz eşiğin
            altına düştüğünde "Az slot kaldı!" mesajı gösterilir.
          </li>
          <li>
            <strong>Güvenlik:</strong> Veritabanındaki gerçek slotlar hiç
            etkilenmez. Sadece görüntüleme katmanında değişiklik yapılır.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default FomoSettings;
